import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type DateRange } from "@src/appV2/lib/Calendar";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { getBookabilityUnmetCriteriaMetadata } from "@src/appV2/OpenShifts/ShiftAction/getBookabilityUnmetCriteriaMetadata";
import { hasInstantBookCriteriaUnmet } from "@src/appV2/OpenShifts/ShiftAction/hasInstantBookCriteriaUnmet";
import { hasUnmetBookabilityCriteria } from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { useShouldShowClipboardOrientationBookability } from "@src/appV2/SkillsAssessment/useShouldShowClipboardOrientationBookability";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isPast, minutesToMilliseconds, parseISO } from "date-fns";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { useIsArrivalTimeEligible } from "../../ArrivalTime/useIsArrivalTimeEligible";
import { calculateMissingCredential } from "../../Documents/utils/calculateMissingCredential";
import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { useDistanceToWorkplace } from "../../Workplace/useDistanceToWorkplace";
import { type WorkerPublicProfile } from "../../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { useIsWorkWithFriendsV2Enabled } from "../../WorkWithFriends/hooks/useIsWorkWithFriendsV2Enabled";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { useRequiresBreakPolicyAcknowledgement } from "../Booking/useRequiresBreakPolicyAcknowledgement";
import { useShiftDurationWithBreak } from "../hooks/useShiftDurationWithBreak";
import { resolveShiftType } from "../resolveShiftType";
import {
  extractBasketSizeLimitRestriction,
  extractLeadTimeThresholdRestriction,
} from "./basket-size-restriction";
import { OpenShiftCard } from "./Card";
import { getOpenShiftCardPillsProps } from "./getOpenShiftCardPillsProps";
import type { Offer, OpenShift, OpenShiftWorkplace } from "./types";
import { useBlockingRequirementsPills } from "./useBlockingRequirementsPills";
import { useGetShiftModal } from "./useGetShiftModal";
import { useLogOpenShiftViewed } from "./useLogOpenShiftViewed";
import { useOpenShiftListDataContext } from "./useOpenShiftListDataContext";
import {
  extractWindowRestriction,
  extractWindowRestrictionScoreThreshold,
  isWindowedOutShift,
} from "./window-restrictions";

interface OnClickParams {
  shift: OpenShift;
  workplace: OpenShiftWorkplace;
  offer?: Offer;
}

interface OpenShiftCardProps {
  shift: OpenShift;
  workers: WorkerPublicProfile[];
  workplace: OpenShiftWorkplace;
  isFavoriteWorkplace?: boolean;
  onClick?: (params: OnClickParams) => void;
  blockingRequirementsContext?: BlockingRequirementsContext;
  sx?: SxProps<Theme>;
  friendsMap?: Map<string, WorkerPublicProfile>;
  recentColleaguesMap?: Map<string, RecentColleague>;
  shiftAuction?: ShiftAuction;
}

export function OpenShiftCardContainer(props: OpenShiftCardProps) {
  const {
    shift,
    workplace,
    isFavoriteWorkplace,
    onClick,
    workers,
    sx,
    friendsMap,
    recentColleaguesMap,
    blockingRequirementsContext,
    shiftAuction,
  } = props;
  const { timeSlot, start, end, qualification, window, priorityTill, isUrgent, bookability } =
    shift.attributes;

  const { shiftsBookabilityMap } = useOpenShiftListDataContext();
  const bookabilityStatus = shiftsBookabilityMap.get(shift.id);

  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([qualification]);
  const qualificationText = qualificationDisplayLabels[0];

  const dateRange: DateRange = { startDate: parseISO(start), endDate: parseISO(end) };
  const shiftType = resolveShiftType({ isUrgent, window, priorityTill });

  const isWorkWithFriendsV2Enabled = useIsWorkWithFriendsV2Enabled();

  // we're gonna rerender this component every minute to check if the priority shift is still valid
  useCurrentDate(minutesToMilliseconds(1));

  // Get auto-refreshed offer and pre-fetch shift details for modals.
  const {
    data: { offer, availableShiftBoost },
    offerIsError,
    offerIsLoading,
    isError,
  } = useGetShiftModal(shift.id, { autoRefreshOffer: !isDefined(shiftAuction) });

  const { formattedDistance, distanceInMiles } = useDistanceToWorkplace({
    workplaceGeoLocation: workplace.attributes.location,
  });

  const { ref, inView } = useLogOpenShiftViewed({
    start,
    end,
    workplaceId: shift.relationships.workplace.data.id,
    shiftId: shift.id,
    hourlyPay: offer ? offer.attributes.pay.amountInMinorUnits / 100 : 0,
    qualification,
    shiftType,
    priorityTill,
    window,
    offerId: offer ? offer.id : undefined,
    isInstantReview: blockingRequirementsContext?.areAllVisibleBlockedDocumentsInstantReview,
    missingDocuments: blockingRequirementsContext?.blockingRequirements.map(
      (requirement) => requirement.requirementId
    ),
    windowedOut: isWindowedOutShift(shift),
    distanceInMiles,
  });

  // Pre-fetch break policy acknowledgement for the shift,
  // so that it's ready when the user clicks the shift card.
  // No need to fetch if there's no offer, as it's not bookable.
  useRequiresBreakPolicyAcknowledgement({
    workplaceId: workplace.id,
    enabled: isDefined(offer) && inView,
  });

  const offerAmount = isDefined(offer) ? offer.attributes.pay.amountInMinorUnits / 100 : undefined;

  const minutesElapsed = calculateMinutesElapsed(dateRange.startDate, new Date());

  const { totalDurationInHours, mandatoryBreakDurationInMinutes, requiresBreak } =
    useShiftDurationWithBreak({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      workplaceId: workplace.id,
      requiresLunchBreak: workplace.attributes.requiresLunchBreak,
      minutesElapsed,
    });

  const workDurationInHours = getShiftWorkDurationInHours(
    { start, end },
    requiresBreak,
    mandatoryBreakDurationInMinutes,
    minutesElapsed
  );

  const disabled = offerIsError || isError;
  useLogEffect(
    APP_V2_USER_EVENTS.OPEN_SHIFTS_CARD_DISABLED,
    {
      shiftId: shift.id,
      shiftError: isError,
      offerError: offerIsError,
    },
    { enabled: disabled && inView }
  );

  const licensePillStatus = isDefined(bookabilityStatus)
    ? getBookabilityUnmetCriteriaMetadata(
        bookabilityStatus,
        BookabilityUnmetCriteria.WORKER_MISSING_LICENSE
      )
    : undefined;
  const hasPaymentsDisabled = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_PAYMENTS_DISABLED,
  ]);
  const hasAgreementToSign = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_UNSIGNED_AGREEMENT,
  ]);
  const isInstantBook = !hasInstantBookCriteriaUnmet(bookabilityStatus);

  const isOrientationRequired = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_NOT_COMPLETED_ORIENTATION,
  ]);

  const isShortLeadTime = useIsArrivalTimeEligible({
    shiftStartIso: start,
    workplaceType: workplace?.attributes?.type,
    isInstantBook,
    workplaceId: workplace?.id ?? "",
    workplaceMsa: workplace?.attributes?.location?.msa ?? "",
  });
  const shouldLeaveNow = isShortLeadTime && isPast(dateRange.startDate);

  const {
    blockingRequirementsPillCount,
    showBlockingRequirementsPill,
    areBlockingRequirementsExpiringSoon,
    blockingRequirementExpiryDate,
  } = useBlockingRequirementsPills({
    blockingRequirementsContext,
    hasPaymentsDisabled,
    hasAgreementToSign,
  });

  const unbookableWindowName = extractWindowRestriction(bookability);
  const unbookableWindowThreshold = extractWindowRestrictionScoreThreshold(bookability);
  const basketSizeLimit = extractBasketSizeLimitRestriction(bookability);
  const leadTimeThreshold = extractLeadTimeThresholdRestriction(bookability);
  const worker = useDefinedWorker();
  const { userId: workerId } = worker;
  const { data: { clipboardScore } = {} } = useGetClipboardScoreProfile(workerId, {
    enabled: Boolean(workerId),
  });
  const hasDemoShiftToComplete = useShouldShowClipboardOrientationBookability(bookabilityStatus);

  const { workplaceCredentialRequirements = [], baseCredentialRequirements = [] } =
    blockingRequirementsContext ?? {};

  const missingCredentialType = calculateMissingCredential([
    ...baseCredentialRequirements,
    ...workplaceCredentialRequirements,
  ]);

  // Calculate pills to show with complete props
  const pillsProps = getOpenShiftCardPillsProps({
    shiftType,
    blockingRequirementsPillContext: {
      blockingRequirementsPillCount,
      showBlockingRequirementsPill,
      areBlockingRequirementsExpiringSoon,
      blockingRequirementExpiryDate,
    },
    missingCredentialType,
    unbookableWindowName,
    unbookableWindowThreshold,
    workerScore: clipboardScore,
    licensePillStatus: licensePillStatus?.status,
    basketSizeLimit,
    leadTimeThreshold,
    hasPaymentsDisabled,
    hasAgreementToSign,
    hasDemoShiftToComplete,
    hasShiftAuction: isDefined(shift.relationships.shiftAuction),
    isOrientationRequired,
  });

  return (
    <OpenShiftCard
      ref={ref}
      shift={shift}
      workers={workers}
      workplace={workplace}
      isFavoriteWorkplace={isFavoriteWorkplace}
      sx={sx}
      friendsMap={friendsMap}
      recentColleaguesMap={recentColleaguesMap}
      timeSlot={timeSlot}
      dateRange={dateRange}
      remainingWorkDurationInHours={workDurationInHours}
      remainingTotalDurationInHours={totalDurationInHours}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      requiresBreak={requiresBreak}
      formattedDistance={formattedDistance}
      disabled={disabled}
      offer={offer}
      offerAmount={offerAmount}
      shiftAuction={shiftAuction}
      availableShiftBoostAmount={availableShiftBoost?.attributes.amount}
      offerIsError={offerIsError}
      offerIsLoading={offerIsLoading}
      qualificationText={qualificationText}
      isLoadingQualification={isLoadingQualification}
      pillsProps={pillsProps}
      isWorkWithFriendsV2Enabled={isWorkWithFriendsV2Enabled}
      isShortLeadTime={isShortLeadTime}
      shouldLeaveNow={shouldLeaveNow}
      minutesElapsed={minutesElapsed}
      onClick={onClick}
    />
  );
}
